<template>
  <header
    ref="headerEl"
    class="bg-card border-b border-border sticky top-0 z-10"
    :class="[progress > 0 ? 'shadow-sm' : '', shrinkReady ? 'overflow-hidden' : '']"
    :style="shrinkReady ? { height: headerHeight + 'px' } : {}"
  >
    <div
      class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
      :style="{ paddingTop: paddingY + 'px', paddingBottom: paddingY + 'px' }"
    >
      <div class="flex items-center gap-3">
        <slot name="icon" :size="iconSize" />
        <div class="min-w-0">
          <h1
            class="font-bold text-foreground truncate"
            :style="{ fontSize: titleFontSize + 'px', lineHeight: titleLineHeight + 'px' }"
          >
            {{ title }}
          </h1>
          <div
            v-if="subtitle"
            class="overflow-hidden"
            :style="{ height: subtitleHeight + 'px', opacity: subtitleOpacity }"
          >
            <p class="text-sm text-muted-foreground">{{ subtitle }}</p>
          </div>
        </div>
      </div>
    </div>
  </header>
  <!-- Spacer absorbs header height change so content below never moves during shrink -->
  <div :style="{ height: spacerHeight + 'px' }"></div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted } from 'vue'

defineProps({
  title: { type: String, required: true },
  subtitle: { type: String, default: '' },
})

const headerEl = ref(null)

// Scroll-driven shrink state
const progress = ref(0) // 0 = fully expanded, 1 = fully compact
let navHeight = 64
const shrinkReady = ref(false)
let expandedHeight = 0
let shrinkRange = 0
const COMPACT_HEIGHT = 40 // py-2 (16px) + text-base line (24px)

// All dimensions interpolated from progress (no CSS transitions)
const paddingY = computed(() => 16 - progress.value * 8) // 16px → 8px
const iconSize = computed(() => 32 - progress.value * 12) // 32px → 20px
const titleFontSize = computed(() => 24 - progress.value * 8) // 24px (text-2xl) → 16px (text-base)
const titleLineHeight = computed(() => 32 - progress.value * 8) // 32px → 24px
const subtitleHeight = computed(() => 20 * (1 - progress.value)) // 20px → 0px
const subtitleOpacity = computed(() => Math.max(0, 1 - progress.value * 3)) // fades out in first third

// Header + spacer always sum to expandedHeight, so content below never moves
const headerHeight = computed(() =>
  expandedHeight ? expandedHeight - progress.value * shrinkRange : 0,
)
const spacerHeight = computed(() => progress.value * shrinkRange)

const onScroll = () => {
  if (!shrinkRange) return
  const scrollY = window.scrollY
  if (scrollY <= navHeight) {
    progress.value = 0
  } else if (scrollY >= navHeight + shrinkRange) {
    progress.value = 1
  } else {
    progress.value = (scrollY - navHeight) / shrinkRange
  }
}

onMounted(async () => {
  const nav = document.querySelector('nav')
  if (nav) navHeight = nav.offsetHeight
  await nextTick()
  if (headerEl.value) {
    expandedHeight = headerEl.value.offsetHeight
    shrinkRange = Math.max(0, expandedHeight - COMPACT_HEIGHT)
    shrinkReady.value = true
    onScroll()
  }
  window.addEventListener('scroll', onScroll, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll)
})

defineExpose({
  getCollapseScrollY: () => navHeight + shrinkRange,
  compactHeight: COMPACT_HEIGHT,
})
</script>
